import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { IoClose } from 'react-icons/io5';
import { usePipeline } from '../../context/PipelineContext';
import { getGlobalParams, setGlobalParam, editFinanceNode, getPipelineByName, updateMultiUnitOASetup } from '../../api/api';
import { notifyError } from '../../errors/notifyError';
import { OP } from '../../api/operations';
import { notifySuccess } from '../../notifications/notifySuccess';
import './SetupUtility.css';

Modal.setAppElement('#root');

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.25rem',
    maxWidth: '1100px',
    width: '95%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative'
  },
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 9999
  }
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <h3 style={{ margin: '8px 0' }}>{title}</h3>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, setValue, type = 'number', step = '0.01', disabled = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => setValue(
        type === 'checkbox'
          ? e.target.checked
          : type === 'number'
            ? (e.target.value === '' ? '' : Number(e.target.value))
            : e.target.value
      )}
      step={type === 'number' ? step : undefined}
      disabled={disabled}
      style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6 }}
    />
  </div>
);

const MULTI_UNIT_BALANCE_MODE_CALCULATED = 'calculated';
const MULTI_UNIT_BALANCE_MODE_MANUAL = 'manual';

const normalizeMultiUnitBalanceMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['manual', 'user_input', 'no_permissible_limit'].includes(normalized)
    ? MULTI_UNIT_BALANCE_MODE_MANUAL
    : MULTI_UNIT_BALANCE_MODE_CALCULATED;
};

const SetupUtility = ({ isOpen, onRequestClose }) => {
  const { session, updateFinanceTariff } = usePipeline();
  const pipelineName = session?.pipeline_name;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, setGlobals] = useState({});
  const [activeTab, setActiveTab] = useState('globals'); // 'globals' | 'finance' | 'multiUnit'

  // Fields
  const [multFactorWind, setMultFactorWind] = useState('');
  const [multFactorSolar, setMultFactorSolar] = useState('');
  const [acdcRatioDefault, setAcdcRatioDefault] = useState('');
  const [transmissionLossDefault, setTransmissionLossDefault] = useState('');
  const [wheelingLossDefault, setWheelingLossDefault] = useState('');
  const [gstInputDefault, setGstInputDefault] = useState(false);
  const [gstPerc, setGstPerc] = useState('');
  // Loan defaults
  const [loanPerc, setLoanPerc] = useState('');
  const [annualRate, setAnnualRate] = useState('');
  const [tenureYears, setTenureYears] = useState('');
  const [moratoriumYears, setMoratoriumYears] = useState('');
  // Depreciation defaults
  const [basicPriceWind, setBasicPriceWind] = useState('');
  const [basicPriceSolar, setBasicPriceSolar] = useState('');
  const [depreciationRate, setDepreciationRate] = useState('');
  const [addnDepRate, setAddnDepRate] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [perUnitDiscomCost, setPerUnitDiscomCost] = useState('');
  // Finance (node-level) inputs tab state (local-only, not global)
  const [financeCalcMode, setFinanceCalcMode] = useState('standard'); // 'standard' | 'gh'
  const [unitsX, setUnitsX] = useState('');
  const [energyCostA, setEnergyCostA] = useState('');
  const [wheelingChargeB, setWheelingChargeB] = useState('');
  const [electricityDutyC, setElectricityDutyC] = useState('');
  const [todChargesD, setTodChargesD] = useState('');
  const [taxE, setTaxE] = useState('');
  const [facF, setFacF] = useState('');
  // Rebate components (stored separately; summed into rebate_G)
  const [bulkConsumptionRebate, setBulkConsumptionRebate] = useState('');
  const [promptPaymentDiscount, setPromptPaymentDiscount] = useState('');
  const [incrementalConsumptionRebate, setIncrementalConsumptionRebate] = useState('');
  const [loadFactorIncentive, setLoadFactorIncentive] = useState('');
  const rebateGTotal =
    Number(bulkConsumptionRebate || 0) +
    Number(promptPaymentDiscount || 0) +
    Number(incrementalConsumptionRebate || 0) +
    Number(loadFactorIncentive || 0);

  // GH DISCOM tariff inputs (sum of per-unit charges; consumption units not used)
  const [ghEnergyCharge, setGhEnergyCharge] = useState('');
  const [ghFuelSurcharge, setGhFuelSurcharge] = useState('');
  const [ghPeakSurcharge, setGhPeakSurcharge] = useState('');
  const [ghSolarPeakDiscount, setGhSolarPeakDiscount] = useState('');
  const [ghPfIncentive, setGhPfIncentive] = useState('');
  const [ghEhvRebate, setGhEhvRebate] = useState('');
  const [ghDemandCharges, setGhDemandCharges] = useState('');
  const [ghElectricityDuty, setGhElectricityDuty] = useState('');
  const [universalSubsidy, setUniversalSubsidy] = useState('');
  const [universalSubsidyLabel, setUniversalSubsidyLabel] = useState('Universal subsidy');
  const [multiUnitNodes, setMultiUnitNodes] = useState([]);
  const [selectedMultiUnitNode, setSelectedMultiUnitNode] = useState('');
  const [multiUnitRows, setMultiUnitRows] = useState([]);
  const [multiUnitBalanceMode, setMultiUnitBalanceMode] = useState(MULTI_UNIT_BALANCE_MODE_CALCULATED);

  const ghReplaceableTariff = (() => {
    const n = (v) => Number(v || 0);
    // Always treat these three as negative inputs
    const solarDisc = -Math.abs(n(ghSolarPeakDiscount));
    const pfInc = -Math.abs(n(ghPfIncentive));
    const ehvReb = -Math.abs(n(ghEhvRebate));
    const units = n(unitsX);
    const subsidyPerUnit = units ? Math.abs(n(universalSubsidy)) / units : 0;
    return n(ghEnergyCharge) + n(ghFuelSurcharge) + n(ghPeakSurcharge) + solarDisc + pfInc + ehvReb + n(ghDemandCharges) + n(ghElectricityDuty) - subsidyPerUnit;
  })();

  const updateMultiUnitRow = (unitId, key, value) => {
    setMultiUnitRows((prev) => prev.map((row) => (
      row.unit_id === unitId ? { ...row, [key]: value } : row
    )));
  };
  const calculatedFormulaBalance = (row) => {
    const permissible = Number(row.permissible_limit || 0);
    const existing = Number(row.existing_oa || 0);
    return Math.max((Number.isFinite(permissible) ? permissible : 0) - (Number.isFinite(existing) ? existing : 0), 0);
  };
  const calcBalance = (row, balanceMode = multiUnitBalanceMode) => {
    if (balanceMode === MULTI_UNIT_BALANCE_MODE_MANUAL) {
      const balance = Number(row.balance || 0);
      return Math.max(Number.isFinite(balance) ? balance : 0, 0);
    }
    return calculatedFormulaBalance(row);
  };
  const calculatedAllocationShare = (row, rows = multiUnitRows) => {
    const totalBalance = rows.reduce((sum, item) => sum + calcBalance(item), 0);
    return totalBalance > 0 ? (calcBalance(row) / totalBalance) * 100 : 0;
  };
  const useManualBalance = multiUnitBalanceMode === MULTI_UNIT_BALANCE_MODE_MANUAL;
  const hasManualAllocation = multiUnitRows.some(
    (row) => row.allocation_share_override_pct !== null
      && row.allocation_share_override_pct !== undefined
      && row.allocation_share_override_pct !== ''
  );
  const allocationShareTotal = multiUnitRows.reduce((sum, row) => {
    const value = hasManualAllocation
      ? Number(row.allocation_share_override_pct)
      : calculatedAllocationShare(row);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const updateAllocationOverride = (unitId, value) => {
    setMultiUnitRows((prev) => prev.map((row) => ({
      ...row,
      allocation_share_override_pct: row.unit_id === unitId
        ? value
        : (row.allocation_share_override_pct ?? calculatedAllocationShare(row, prev)),
    })));
  };
  const resetAllocationOverrides = () => {
    setMultiUnitRows((prev) => prev.map((row) => ({
      ...row,
      allocation_share_override_pct: null,
    })));
  };
  const updateMultiUnitBalanceMode = (checked) => {
    const nextMode = checked ? MULTI_UNIT_BALANCE_MODE_MANUAL : MULTI_UNIT_BALANCE_MODE_CALCULATED;
    if (nextMode === MULTI_UNIT_BALANCE_MODE_MANUAL) {
      setMultiUnitRows((prev) => prev.map((row) => ({
        ...row,
        balance: row.balance === '' || row.balance === null || row.balance === undefined
          ? calculatedFormulaBalance(row)
          : row.balance,
      })));
    }
    setMultiUnitBalanceMode(nextMode);
  };

  // Operational costs groups moved back to Operational Costs node modal
  const [discountRate, setDiscountRate] = useState('');
  // Operating charges and additional options are configured at node level

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !pipelineName || pipelineName === 'Unsaved_Pipeline') return;
      setLoading(true);
      try {
        const gp = await getGlobalParams(pipelineName);
        setGlobals(gp || {});
        setMultFactorWind(gp?.mult_factor_wind ?? '');
        setMultFactorSolar(gp?.mult_factor_solar ?? '');
        setAcdcRatioDefault(gp?.acdc_ratio ?? '');
        setTransmissionLossDefault(gp?.transmission_loss_perc ?? '');
        setWheelingLossDefault(gp?.wheeling_loss_perc ?? '');
        setGstInputDefault(!!gp?.gst_input);
        setGstPerc(gp?.gst_perc ?? '');
        // Loan defaults
        setLoanPerc(gp?.loan_perc ?? '');
        setAnnualRate(gp?.annual_rate ?? '');
        setTenureYears(gp?.tenure_years ?? '');
        setMoratoriumYears(gp?.moratorium_years ?? '');
        // Depreciation defaults
        setBasicPriceWind(gp?.Basic_price_wind ?? '');
        setBasicPriceSolar(gp?.Basic_price_solar ?? '');
        setDepreciationRate(gp?.depreciation_rate ?? '');
        setAddnDepRate(gp?.addn_dep_rate ?? '');
        setTaxRate(gp?.tax_rate ?? '');
        setPerUnitDiscomCost(gp?.per_unit_discom_cost ?? '');
        // Basic configuration subset needed here
        setDiscountRate(gp?.discount_rate ?? '');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, pipelineName]);

  // Populate Finance tab fields from catalog for the selected Finance node
  useEffect(() => {
    const loadFinanceNodeParams = async () => {
      try {
        if (!isOpen || activeTab !== 'finance' || !pipelineName || pipelineName === 'Unsaved_Pipeline') return;
        const sel = session?.selected_node;
        const sn = (session?.nodes || []).find(n => n.node_name === sel);
        if (!sn || sn.node_type !== 'finance') {
          // clear fields if no finance node selected
          setFinanceCalcMode('standard');
          setUnitsX(''); setEnergyCostA(''); setWheelingChargeB(''); setElectricityDutyC(''); setTodChargesD(''); setTaxE(''); setFacF('');
          setBulkConsumptionRebate(''); setPromptPaymentDiscount(''); setIncrementalConsumptionRebate('');
          setGhEnergyCharge(''); setGhFuelSurcharge(''); setGhPeakSurcharge(''); setGhSolarPeakDiscount('');
          setGhPfIncentive(''); setGhEhvRebate(''); setGhDemandCharges(''); setGhElectricityDuty('');
          setUniversalSubsidy('');
          setUniversalSubsidyLabel('Universal subsidy');
          return;
        }
        const doc = await getPipelineByName(pipelineName);
        const catalog = (doc && doc.catalog) || {};
        const k = (suffix) => `params:${suffix}_${sel}`;
        const readNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v));
        const mode = (catalog[k('discom_tariff_calc_mode')] ?? 'standard');
        setFinanceCalcMode(String(mode).toLowerCase() === 'gh' ? 'gh' : 'standard');
        setUnitsX(readNum(catalog[k('units_X')]));
        setEnergyCostA(readNum(catalog[k('energy_cost_A')]));
        setWheelingChargeB(readNum(catalog[k('wheeling_charge_B')]));
        setElectricityDutyC(readNum(catalog[k('electricity_duty_C')]));
        setTodChargesD(readNum(catalog[k('tod_charges_D')]));
        setTaxE(readNum(catalog[k('tax_E')]));
        setFacF(readNum(catalog[k('fac_F')]));
        // GH fields
        setGhEnergyCharge(readNum(catalog[k('gh_energy_charge')]));
        setGhFuelSurcharge(readNum(catalog[k('gh_fuel_surcharge')]));
        setGhPeakSurcharge(readNum(catalog[k('gh_peak_surcharge')]));
        setGhSolarPeakDiscount(readNum(catalog[k('gh_solar_peak_discount')]));
        setGhPfIncentive(readNum(catalog[k('gh_pf_incentive')]));
        setGhEhvRebate(readNum(catalog[k('gh_ehv_rebate')]));
        setGhDemandCharges(readNum(catalog[k('gh_demand_charges')]));
        setGhElectricityDuty(readNum(catalog[k('gh_electricity_duty')]));
        setUniversalSubsidy(readNum(catalog[k('universal_subsidy')]));
        setUniversalSubsidyLabel(String(catalog[k('universal_subsidy_label')] || '').trim() || 'Universal subsidy');
        // Rebate components (new); fall back to legacy total if components missing
        const bulk = readNum(catalog[k('bulk_consumption_rebate')]);
        const prompt = readNum(catalog[k('prompt_payment_discount')]);
        const incr = readNum(catalog[k('incremental_consumption_rebate')]);
        const loadFactor = readNum(catalog[k('load_factor_incentive')]);
        const legacyTotal = readNum(catalog[k('rebate_G')]);
        if (bulk === '' && prompt === '' && incr === '' && loadFactor === '' && legacyTotal !== '') {
          // Keep components blank but still show total via computed field
          setBulkConsumptionRebate('');
          setPromptPaymentDiscount('');
          setIncrementalConsumptionRebate('');
          setLoadFactorIncentive('');
        } else {
          setBulkConsumptionRebate(bulk);
          setPromptPaymentDiscount(prompt);
          setIncrementalConsumptionRebate(incr);
          setLoadFactorIncentive(loadFactor);
        }
      } catch (e) {
        // ignore fetch issues; keep current inputs
        console.warn('Failed loading finance inputs for selected node', e);
      }
    };
    loadFinanceNodeParams();
  }, [isOpen, activeTab, pipelineName, session?.selected_node, session?.nodes]);

  useEffect(() => {
    const loadMultiUnitNodes = async () => {
      if (!isOpen || activeTab !== 'multiUnit' || !pipelineName || pipelineName === 'Unsaved_Pipeline') return;
      setLoading(true);
      try {
        const doc = await getPipelineByName(pipelineName);
        const catalog = doc?.catalog || {};
        const nodes = doc?.pipeline?.nodes || [];
        const found = nodes
          .filter((node) => node?.func === 'consumption_input')
          .map((node) => {
            const nodeName = node.name;
            const mode = String(catalog[`params:consumption_input_mode_${nodeName}`] || '').toLowerCase();
            if (mode !== 'multi_unit_slot_monthly') return null;
            let payload = {};
            try {
              const raw = catalog[`params:slot_monthly_data_${nodeName}`];
              payload = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
            } catch (error) {
              payload = {};
            }
            const balanceMode = normalizeMultiUnitBalanceMode(payload.balance_mode ?? payload.balanceMode);
            return {
              nodeName,
              units: Array.isArray(payload.units) ? payload.units : [],
              balanceMode,
            };
          })
          .filter(Boolean);
        setMultiUnitNodes(found);
        const preferred = found.find((item) => item.nodeName === selectedMultiUnitNode) || found[0];
        setSelectedMultiUnitNode(preferred?.nodeName || '');
        setMultiUnitBalanceMode(preferred?.balanceMode || MULTI_UNIT_BALANCE_MODE_CALCULATED);
        setMultiUnitRows((preferred?.units || []).map((unit) => ({
          unit_id: unit.unit_id || unit.unitId,
          unit_name: unit.unit_name || unit.unitName || '',
          location: unit.location || '',
          cd: unit.cd ?? '',
          ct: unit.ct ?? '',
          voltage: unit.voltage ?? '',
          permissible_limit: unit.permissible_limit ?? unit.permissibleLimit ?? '',
          existing_oa: unit.existing_oa ?? unit.existingOa ?? '',
          existing_oa_re_units: unit.existing_oa_re_units ?? unit.existingOaReUnits ?? 0,
          existing_btm_units: unit.existing_btm_units ?? unit.existingBtmUnits ?? 0,
          balance: unit.balance ?? '',
          allocation_share_override_pct: unit.allocation_share_override_pct
            ?? unit.allocationShareOverridePct
            ?? null,
        })));
      } catch (error) {
        console.error('Failed loading multi-unit setup', error);
        notifyError(error);
      } finally {
        setLoading(false);
      }
    };
    loadMultiUnitNodes();
  }, [isOpen, activeTab, pipelineName, selectedMultiUnitNode]);

  const saveGlobals = async () => {
    if (!pipelineName || pipelineName === 'Unsaved_Pipeline') return;
    setSaving(true);
    try {
      // Save only fields owned by Setup Utility (plus basic subset)
      const updates = [
        ['mult_factor_wind', multFactorWind],
        ['mult_factor_solar', multFactorSolar],
        ['acdc_ratio', acdcRatioDefault],
        ['transmission_loss_perc', transmissionLossDefault],
        ['wheeling_loss_perc', wheelingLossDefault],
        ['gst_input', Boolean(gstInputDefault)],
        ['gst_perc', gstPerc],
        // Loan defaults
        ['loan_perc', loanPerc],
        ['annual_rate', annualRate],
        ['tenure_years', tenureYears],
        ['moratorium_years', moratoriumYears],
        // Depreciation defaults
        ['Basic_price_wind', basicPriceWind],
        ['Basic_price_solar', basicPriceSolar],
        ['depreciation_rate', depreciationRate],
        ['addn_dep_rate', addnDepRate],
        ['tax_rate', taxRate],
        ['per_unit_discom_cost', perUnitDiscomCost],
        ['discount_rate', discountRate],
        // no op-cost fields here
      ];
      const applySet = new Set([
        'mult_factor_wind',
        'mult_factor_solar',
        'acdc_ratio',
        'transmission_loss_perc',
        'wheeling_loss_perc',
        'gst_input',
        'gst_perc',
        // Loan defaults
        'loan_perc',
        'annual_rate',
        'tenure_years',
        'moratorium_years',
        // Depreciation defaults
        'Basic_price_wind',
        'Basic_price_solar',
        'depreciation_rate',
        'addn_dep_rate',
        'tax_rate',
        'per_unit_discom_cost',
        'discount_rate',
      ]);

      for (const [key, val] of updates) {
        if (val !== '' && val !== null && val !== undefined) {
          const applyToNodes = applySet.has(key);
          await setGlobalParam(pipelineName, key, val, true, applyToNodes);
        }
      }
      notifySuccess('Setup values saved successfully.');
    } catch (e) {
      console.error('Failed to save setup values', e);
      notifyError(e, { operation: OP.params.setGlobal });
    } finally {
      setSaving(false);
    }
  };

  const saveMultiUnitOA = async () => {
    if (!pipelineName || pipelineName === 'Unsaved_Pipeline' || !selectedMultiUnitNode) return;
    if (useManualBalance) {
      const invalidBalance = multiUnitRows.find((row) => {
        const value = Number(row.balance);
        return row.balance === '' || row.balance === null || row.balance === undefined || !Number.isFinite(value) || value < 0;
      });
      if (invalidBalance) {
        notifyError(new Error(`Enter a non-negative balance for ${invalidBalance.unit_name || 'each unit'}.`), {
          toastId: 'setupUtility.multiUnitMissingBalance',
        });
        return;
      }
    } else {
      const invalid = multiUnitRows.find((row) => row.permissible_limit === '' || row.existing_oa === '');
      if (invalid) {
        notifyError(new Error(`Enter permissible limit and existing OA for ${invalid.unit_name || 'each unit'}.`), {
          toastId: 'setupUtility.multiUnitMissingOA',
        });
        return;
      }
    }
    if (hasManualAllocation) {
      const invalidAllocation = multiUnitRows.find((row) => {
        const value = Number(row.allocation_share_override_pct);
        return row.allocation_share_override_pct === null
          || row.allocation_share_override_pct === ''
          || !Number.isFinite(value)
          || value < 0
          || value > 100;
      });
      if (invalidAllocation) {
        notifyError(new Error(`Enter an allocation percentage between 0 and 100 for ${invalidAllocation.unit_name || 'each unit'}.`), {
          toastId: 'setupUtility.multiUnitInvalidAllocation',
        });
        return;
      }
      if (Math.abs(allocationShareTotal - 100) > 0.01) {
        notifyError(new Error(`Final allocation percentages must total 100%. Current total: ${allocationShareTotal.toFixed(4)}%.`), {
          toastId: 'setupUtility.multiUnitAllocationTotal',
        });
        return;
      }
    }
    setSaving(true);
    try {
      await updateMultiUnitOASetup(
        pipelineName,
        selectedMultiUnitNode,
        multiUnitRows.map((row) => ({
          unit_id: row.unit_id,
          permissible_limit: Number(row.permissible_limit || 0),
          existing_oa: Number(row.existing_oa || 0),
          existing_oa_re_units: Number(row.existing_oa_re_units || 0),
          existing_btm_units: Number(row.existing_btm_units || 0),
          balance: useManualBalance ? Number(row.balance || 0) : calcBalance(row),
          location: row.location || '',
          cd: Number(row.cd || 0),
          ct: Number(row.ct || 0),
          voltage: Number(row.voltage || 0),
          allocation_share_override_pct: hasManualAllocation
            ? Number(row.allocation_share_override_pct)
            : null,
        })),
        multiUnitBalanceMode
      );
      notifySuccess('Multi-unit OA setup saved successfully.');
    } catch (error) {
      console.error('Failed saving multi-unit OA setup', error);
      notifyError(error, { operation: OP.node.appendConsumptionInput });
    } finally {
      setSaving(false);
    }
  };

  const saveFinanceNodeParams = async () => {
    // Validate
    if (!pipelineName || pipelineName === 'Unsaved_Pipeline') return;
    // determine selected finance node
    const selected = session?.selected_node;
    const selectedNode = (session?.nodes || []).find(n => n.node_name === selected);
    const finNodeName = selectedNode && selectedNode.node_type === 'finance' ? selectedNode.node_name : null;
    if (!finNodeName) {
      notifyError(new Error('Select a Finance node on the canvas to save these inputs.'), {
        toastId: 'setupUtility.selectFinanceNode',
      });
      return;
    }
    try {
      const payload = {
        discomTariffCalcMode: financeCalcMode,
        unitsX: unitsX === '' ? null : unitsX,
        energyCostA: energyCostA === '' ? null : energyCostA,
        wheelingChargeB: wheelingChargeB === '' ? null : wheelingChargeB,
        electricityDutyC: electricityDutyC === '' ? null : electricityDutyC,
        todChargesD: todChargesD === '' ? null : todChargesD,
        taxE: taxE === '' ? null : taxE,
        facF: facF === '' ? null : facF,
        // keep legacy total in sync (used in finance tariff computation)
        rebateG: rebateGTotal,
        // GH inputs (optional; used when calc mode = 'gh')
        ghEnergyCharge: ghEnergyCharge === '' ? null : ghEnergyCharge,
        ghFuelSurcharge: ghFuelSurcharge === '' ? null : ghFuelSurcharge,
        ghPeakSurcharge: ghPeakSurcharge === '' ? null : ghPeakSurcharge,
        ghSolarPeakDiscount: ghSolarPeakDiscount === '' ? null : ghSolarPeakDiscount,
        ghPfIncentive: ghPfIncentive === '' ? null : ghPfIncentive,
        ghEhvRebate: ghEhvRebate === '' ? null : ghEhvRebate,
        ghDemandCharges: ghDemandCharges === '' ? null : ghDemandCharges,
        ghElectricityDuty: ghElectricityDuty === '' ? null : ghElectricityDuty,
        // store components separately for Billing Analytics
        bulkConsumptionRebate: bulkConsumptionRebate === '' ? null : bulkConsumptionRebate,
        promptPaymentDiscount: promptPaymentDiscount === '' ? null : promptPaymentDiscount,
        incrementalConsumptionRebate: incrementalConsumptionRebate === '' ? null : incrementalConsumptionRebate,
        loadFactorIncentive: loadFactorIncentive === '' ? null : loadFactorIncentive,
        universalSubsidy: universalSubsidy === '' ? 0 : universalSubsidy,
        universalSubsidyLabel: String(universalSubsidyLabel || '').trim() || 'Universal subsidy',
      };
      await editFinanceNode(pipelineName, finNodeName, payload);
      // Update displayed computed tariff locally so Finance modal shows value without reload
      try {
        let computed = 0;
        if (financeCalcMode === 'gh') {
          computed = ghReplaceableTariff;
        } else {
          const x = Number(unitsX || 0);
          const A = Number(energyCostA || 0);
          const B = Number(wheelingChargeB || 0);
          const C = Number(electricityDutyC || 0);
          const D = Number(todChargesD || 0);
          const E = Number(taxE || 0);
          const F = Number(facF || 0);
          const G = Number(rebateGTotal || 0);
          const subsidy = Math.abs(Number(universalSubsidy || 0));
          computed = x ? (A + B + C + D + E + F - G - subsidy) / x : 0;
        }
        const rounded = Math.round(Number(computed) * 10000) / 10000;
        updateFinanceTariff(finNodeName, rounded);
      } catch (e) {
        // ignore local compute errors
      }
    } catch (e) {
      console.error('Failed to save finance node params', e);
      notifyError(e, { operation: OP.node.editFinance });
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} style={customStyles} contentLabel="Setup Utility" shouldCloseOnOverlayClick shouldCloseOnEsc ariaHideApp={false}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Setup Utility</h2>
          <IoClose size={22} onClick={onRequestClose} style={{ cursor: 'pointer' }} />
        </div>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, margin: '8px 0 12px 0' }}>
          <button
            onClick={() => setActiveTab('globals')}
            className="modal-button"
            style={{
              padding: '6px 10px',
              background: activeTab === 'globals' ? '#3b82f6' : '#e5e7eb',
              color: activeTab === 'globals' ? '#fff' : '#111',
              border: 'none', borderRadius: 6
            }}
          >General</button>
          <button
            onClick={() => setActiveTab('finance')}
            className="modal-button"
            style={{
              padding: '6px 10px',
              background: activeTab === 'finance' ? '#3b82f6' : '#e5e7eb',
              color: activeTab === 'finance' ? '#fff' : '#111',
              border: 'none', borderRadius: 6
            }}
          >Finance</button>
          <button
            onClick={() => setActiveTab('multiUnit')}
            className="modal-button"
            style={{
              padding: '6px 10px',
              background: activeTab === 'multiUnit' ? '#3b82f6' : '#e5e7eb',
              color: activeTab === 'multiUnit' ? '#fff' : '#111',
              border: 'none', borderRadius: 6
            }}
          >Multi-Unit OA Setup</button>
        </div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            {activeTab === 'globals' && (
              <>
                <Section title="Basic configuration">
                  <Field label="Basic Price - Wind (Cr)" value={basicPriceWind} setValue={setBasicPriceWind} />
                  <Field label="Basic Price - Solar (Cr)" value={basicPriceSolar} setValue={setBasicPriceSolar} />
                  <Field label="Scale factor - Wind" value={multFactorWind} setValue={setMultFactorWind} />
                  <Field label="Scale factor - Solar" value={multFactorSolar} setValue={setMultFactorSolar} />
                  <Field label="Transmission loss percentage" value={transmissionLossDefault} setValue={setTransmissionLossDefault} />
                  <Field label="Wheeling loss percentage" value={wheelingLossDefault} setValue={setWheelingLossDefault} />
                  <Field label="AC/DC ratio" value={acdcRatioDefault} setValue={setAcdcRatioDefault} />
                  <Field label="Depreciation Rate (IT WDV %)" value={depreciationRate} setValue={setDepreciationRate} />
                  <Field label="Additional Depreciation Rate (%)" value={addnDepRate} setValue={setAddnDepRate} />
                  <Field label="Tax Rate (%)" value={taxRate} setValue={setTaxRate} />
                  <Field label="Replaceable tariff (INR)" value={perUnitDiscomCost} setValue={setPerUnitDiscomCost} />
                  <Field label="NPV Discount rate (%)" value={discountRate} setValue={setDiscountRate} />
                </Section>
                <Section title="Loan configuration">
                  <Field label="Loan Percentage (% of Capex)" value={loanPerc} setValue={setLoanPerc} />
                  <Field label="Annual Interest Rate (%)" value={annualRate} setValue={setAnnualRate} />
                  <Field label="Tenure (Years)" value={tenureYears} setValue={setTenureYears} step="1" />
                  <Field label="Moratorium Period (Years)" value={moratoriumYears} setValue={setMoratoriumYears} step="1" />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>GST input credit</label>
                    <input
                      type="checkbox"
                      checked={!!gstInputDefault}
                      onChange={(e) => setGstInputDefault(e.target.checked)}
                      style={{ width: 18, height: 18 }}
                    />
                  </div>
                  <Field label="GST Percentage (%)" value={gstPerc} setValue={setGstPerc} />
                </Section>
                {/* Operational costs are configured in the Operational Costs node modal */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                  <button onClick={onRequestClose} className="modal-button">Close</button>
                  <button onClick={saveGlobals} className="modal-button" disabled={saving || !pipelineName || pipelineName === 'Unsaved_Pipeline'}>{saving ? 'Saving...' : 'Save'}</button>
                </div>
              </>
            )}
            {activeTab === 'multiUnit' && (
              <>
                {!multiUnitNodes.length ? (
                  <div style={{ color: '#555', padding: '12px 0' }}>
                    No multi-unit consumption node is configured in this pipeline.
                  </div>
                ) : (
                  <div className="setup-utility-multi-unit">
                    <div className="setup-utility-multi-unit-node-select">
                      <label htmlFor="multi-unit-consumption-node">Consumption node</label>
                      <select
                        id="multi-unit-consumption-node"
                        value={selectedMultiUnitNode}
                        onChange={(e) => setSelectedMultiUnitNode(e.target.value)}
                      >
                        {multiUnitNodes.map((item) => (
                          <option key={item.nodeName} value={item.nodeName}>{item.nodeName}</option>
                        ))}
                      </select>
                    </div>
                    <div className="setup-utility-allocation-status">
                      <span>
                        Allocation mode: <strong>{hasManualAllocation ? 'Manual' : 'Calculated'}</strong>
                      </span>
                      <label className="setup-utility-balance-toggle">
                        <input
                          type="checkbox"
                          checked={useManualBalance}
                          onChange={(e) => updateMultiUnitBalanceMode(e.target.checked)}
                        />
                        <span>No permissible limit applies</span>
                      </label>
                      <span className={Math.abs(allocationShareTotal - 100) <= 0.01 ? '' : 'invalid'}>
                        Total: <strong>{allocationShareTotal.toFixed(4)}%</strong>
                      </span>
                      <button
                        type="button"
                        className="modal-button"
                        onClick={resetAllocationOverrides}
                        disabled={!hasManualAllocation}
                      >
                        Reset to calculated
                      </button>
                    </div>
                    <div className="summary-table-wrapper">
                      <table className="summary-table setup-utility-multi-unit-table">
                        <colgroup>
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '8%' }} />
                          <col style={{ width: '11%' }} />
                          <col style={{ width: '11%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '9%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Unit</th>
                            <th>Location</th>
                            <th className="numeric-col">CD</th>
                            <th className="numeric-col">CT</th>
                            <th className="numeric-col">Voltage</th>
                            <th className="numeric-col" title="Permissible Limit">Perm. Limit</th>
                            <th className="numeric-col" title="Existing OA capacity used for headroom">Existing OA MW</th>
                            <th className="numeric-col" title="Annual existing OA renewable generation">OA RE Units</th>
                            <th className="numeric-col" title="Annual existing rooftop BTM generation">BTM Units</th>
                            <th className="numeric-col">Balance</th>
                            <th className="numeric-col" title="Balance-based allocation percentage">Calculated %</th>
                            <th className="numeric-col" title="Percentage used by subsequent runs">Final Allocation %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {multiUnitRows.map((row) => (
                            <tr key={row.unit_id}>
                              <td className="unit-col">{row.unit_name}</td>
                              <td>
                                <input
                                  type="text"
                                  value={row.location}
                                  onChange={(e) => updateMultiUnitRow(row.unit_id, 'location', e.target.value)}
                                />
                              </td>
                              {['cd', 'ct', 'voltage', 'permissible_limit', 'existing_oa'].map((key) => (
                                <td key={key} className="numeric-col">
                                  <input
                                    type="number"
                                    value={row[key]}
                                    disabled={useManualBalance && ['permissible_limit', 'existing_oa'].includes(key)}
                                    onChange={(e) => updateMultiUnitRow(row.unit_id, key, e.target.value)}
                                  />
                                </td>
                              ))}
                              {['existing_oa_re_units', 'existing_btm_units'].map((key) => (
                                <td key={key} className="numeric-col">
                                  <input
                                    type="number"
                                    min="0"
                                    value={row[key]}
                                    onChange={(e) => updateMultiUnitRow(row.unit_id, key, e.target.value)}
                                  />
                                </td>
                              ))}
                              <td className={useManualBalance ? 'numeric-col' : 'balance-col'}>
                                {useManualBalance ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    aria-label={`Balance for ${row.unit_name}`}
                                    value={row.balance}
                                    onChange={(e) => updateMultiUnitRow(row.unit_id, 'balance', e.target.value)}
                                  />
                                ) : (
                                  calcBalance(row).toLocaleString(undefined, { maximumFractionDigits: 3 })
                                )}
                              </td>
                              <td className="balance-col">
                                {calculatedAllocationShare(row).toLocaleString(undefined, { maximumFractionDigits: 4 })}%
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.0001"
                                  aria-label={`Final allocation percentage for ${row.unit_name}`}
                                  value={row.allocation_share_override_pct ?? calculatedAllocationShare(row)}
                                  onChange={(e) => updateAllocationOverride(
                                    row.unit_id,
                                    e.target.value === '' ? '' : Number(e.target.value)
                                  )}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="setup-utility-multi-unit-actions">
                      <button onClick={onRequestClose} className="modal-button">Close</button>
                      <button onClick={saveMultiUnitOA} className="modal-button" disabled={saving || !selectedMultiUnitNode}>
                        {saving ? 'Saving...' : 'Save OA setup'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            {activeTab === 'finance' && (
              <>
                <div style={{ marginTop: 4 }}>
                  <h3 style={{ margin: '8px 0' }}>Discom tariff calculation</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {/* Node name is inferred from the currently selected Finance node on canvas */}
                    <div style={{ display: 'flex', alignItems: 'center', gridColumn: '1 / span 2', color: '#555' }}>
                      {(() => {
                        const sel = session?.selected_node;
                        const sn = (session?.nodes || []).find(n => n.node_name === sel);
                        const isFinance = !!sn && sn.node_type === 'finance';
                        return <span>Selected node: <strong>{isFinance ? sel : 'None (click a Finance node to select)'}</strong></span>;
                      })()}
                    </div>
                    {/* Mode toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, gridColumn: '1 / span 2' }}>
                      <button
                        onClick={() => setFinanceCalcMode('standard')}
                        className="modal-button"
                        style={{
                          padding: '6px 10px',
                          background: financeCalcMode === 'standard' ? '#111827' : '#e5e7eb',
                          color: financeCalcMode === 'standard' ? '#fff' : '#111',
                          border: 'none',
                          borderRadius: 6
                        }}
                      >
                        Standard
                      </button>
                      <button
                        onClick={() => setFinanceCalcMode('gh')}
                        className="modal-button"
                        style={{
                          padding: '6px 10px',
                          background: financeCalcMode === 'gh' ? '#111827' : '#e5e7eb',
                          color: financeCalcMode === 'gh' ? '#fff' : '#111',
                          border: 'none',
                          borderRadius: 6
                        }}
                      >
                        GH discom tariff calculation
                      </button>
                    </div>

                    {financeCalcMode === 'standard' ? (
                      <>
                        <Field label="Units consumed (X)" value={unitsX} setValue={setUnitsX} />
                        <Field label="Energy Cost (A)" value={energyCostA} setValue={setEnergyCostA} />
                        <Field label="Wheeling Charge (B)" value={wheelingChargeB} setValue={setWheelingChargeB} />
                        <Field label="Electricity Duty (C)" value={electricityDutyC} setValue={setElectricityDutyC} />
                        <Field label="TOD Charges (D)" value={todChargesD} setValue={setTodChargesD} />
                        <Field label="Tax (E)" value={taxE} setValue={setTaxE} />
                        <Field label="FAC (F)" value={facF} setValue={setFacF} />
                        <Field label="Bulk Consumption rebate (part of Rebate G)" value={bulkConsumptionRebate} setValue={setBulkConsumptionRebate} />
                        <Field label="Prompt payment discount (part of Rebate G)" value={promptPaymentDiscount} setValue={setPromptPaymentDiscount} />
                        <Field label="Incremental consumption rebate (part of Rebate G)" value={incrementalConsumptionRebate} setValue={setIncrementalConsumptionRebate} />
                        <Field label="Load factor incentive (part of Rebate G)" value={loadFactorIncentive} setValue={setLoadFactorIncentive} />
                        <Field label="Universal subsidy name" type="text" value={universalSubsidyLabel} setValue={setUniversalSubsidyLabel} />
                        <Field label="Universal subsidy (INR)" value={universalSubsidy} setValue={setUniversalSubsidy} />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Rebate (G) total (auto)</label>
                          <input
                            type="number"
                            value={rebateGTotal}
                            readOnly
                            style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6, background: '#f9fafb' }}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Computed Discom tariff (auto)</label>
                          <input
                            type="number"
                            value={(() => {
                              const x = Number(unitsX || 0);
                              const A = Number(energyCostA || 0);
                              const B = Number(wheelingChargeB || 0);
                              const C = Number(electricityDutyC || 0);
                              const D = Number(todChargesD || 0);
                              const E = Number(taxE || 0);
                              const F = Number(facF || 0);
                              const G = Number(rebateGTotal || 0);
                              const subsidy = Math.abs(Number(universalSubsidy || 0));
                              const computed = x ? (A + B + C + D + E + F - G - subsidy) / x : 0;
                              return Math.round(computed * 10000) / 10000;
                            })()}
                            readOnly
                            style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6, background: '#f9fafb' }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Field label="Units consumed (X)" value={unitsX} setValue={setUnitsX} />
                        <Field label="Universal subsidy name" type="text" value={universalSubsidyLabel} setValue={setUniversalSubsidyLabel} />
                        <Field label="Universal subsidy (INR)" value={universalSubsidy} setValue={setUniversalSubsidy} />
                        <Field label="Energy Charge" value={ghEnergyCharge} setValue={setGhEnergyCharge} />
                        <Field label="Fuel Surcharge latest" value={ghFuelSurcharge} setValue={setGhFuelSurcharge} />
                        <Field label="Peak Surcharge" value={ghPeakSurcharge} setValue={setGhPeakSurcharge} />
                        <Field label="Solar Peak 4 hrs Discount (treated as negative)" value={ghSolarPeakDiscount} setValue={setGhSolarPeakDiscount} />
                        <Field label="PF Incentive (treated as negative)" value={ghPfIncentive} setValue={setGhPfIncentive} />
                        <Field label="EHV Rebate (treated as negative)" value={ghEhvRebate} setValue={setGhEhvRebate} />
                        <Field label="Per unit Demand Charges" value={ghDemandCharges} setValue={setGhDemandCharges} />
                        <Field label="Electricity Duty" value={ghElectricityDuty} setValue={setGhElectricityDuty} />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <label style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Replaceable tariff (auto)</label>
                          <input
                            type="number"
                            value={Math.round(Number(ghReplaceableTariff) * 10000) / 10000}
                            readOnly
                            style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6, background: '#f9fafb' }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                    <button onClick={saveFinanceNodeParams} className="modal-button" disabled={!pipelineName || pipelineName === 'Unsaved_Pipeline'}>Save finance inputs</button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default SetupUtility;
